import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';

import { PrimaryButton } from '@/components/buttons/PrimaryButton';
import { InputItem } from '@/components/form/Input/InputItem';
import { TermsConditionsAndPrivacyPolicyLink } from '@/components/form/TermsConditionsAndPrivacyPolicyLink/TermsConditionsAndPrivacyPolicyLink';
import { TextareaItem } from '@/components/form/Textarea/TextareaItem';

import { BasicInquiry, basicInquiryFormSchema } from '../types';

type BasicInquiryFormProps = {
  onSubmit: (data: BasicInquiry) => void;
  isLoading: boolean;
};

export function BasicInquiryForm({
  onSubmit,
  isLoading,
}: BasicInquiryFormProps) {
  const t = useTranslations('forms');
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<BasicInquiry>({
    resolver: yupResolver(basicInquiryFormSchema),
    mode: 'onBlur',
  });

  return (
    <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col">
        <div className="flex flex-col gap-3 mb-7 sm:mb-5 sm:grid sm:grid-cols-4">
          <InputItem
            className="col-span-2"
            label={t('firstName')}
            {...register('firstName')}
            error={errors.firstName && t(errors.firstName.message)}
            data-testid="input-firstname"
            required
          />
          <InputItem
            className="col-span-2"
            label={t('lastName')}
            {...register('lastName')}
            error={errors.lastName && t(errors.lastName.message)}
            data-testid="input-lastname"
            required
          />
          <InputItem
            className="col-span-2"
            label={t('email')}
            {...register('email')}
            error={errors.email && t(errors.email.message)}
            data-testid="input-email"
            required
          />
          <InputItem
            className="col-span-2"
            label={t('phone')}
            {...register('phone')}
            error={errors.phone && t(errors.phone.message)}
            data-testid="input-phone"
            required
          />
          <InputItem
            className="col-span-2"
            label={t('company')}
            {...register('company')}
            error={errors.company && t(errors.company.message)}
            data-testid="input-company"
            required
          />
          <TextareaItem
            className="col-span-4"
            label={t('comments')}
            {...register('comments')}
            error={errors.comments && t(errors.comments.message)}
            data-testid="input-comments"
          />
        </div>
      </div>

      <TermsConditionsAndPrivacyPolicyLink />

      <PrimaryButton
        className="self-end w-full mt-8 sm:w-fit"
        type="submit"
        data-testid="submit-application-form"
        disabled={!isValid}
        isLoading={isLoading}
      >
        {t('submit')}
      </PrimaryButton>
    </form>
  );
}
